import Service from '@ember/service';
import { service } from '@ember/service';
import { hash } from 'rsvp';
import PrettyNum from '../utils/pretty-num-wrapper';
import Constants from '../utils/constants';
import mathRound from '../helpers/math-round';
import $ from 'jquery';
import jsPDF from 'jspdf';
import { arial } from '../utils/fonts/ARIAL-normal';
import { arialBold } from '../utils/fonts/ARIALBD-bold';
import { arialItalic } from '../utils/fonts/ARIALI-italic';

const FONT_SIZE_TITLE = 24;
const FONT_SIZE_H1 = 16;
const FONT_SIZE_H2 = 13;
const FONT_SIZE_NORMAL = 11;

const MARGIN = 15; // in mm
const LETTER_HEIGHT = 279;
const LETTER_WIDTH = 216;
const MARGIN_RIGHT = LETTER_WIDTH - MARGIN;
const MARGIN_CENTER = MARGIN + (MARGIN_RIGHT - MARGIN) / 2;
const MARGIN_BOTTOM = LETTER_HEIGHT - MARGIN;

//#region local functions

function convertSVGToImage(svgElem) {
    let imageData;
    const baseHeight = svgElem.height.baseVal.value;
    const baseWidth = svgElem.width.baseVal.value;
    let scaler = 1500 / Math.max(baseHeight, baseWidth); // higher = better image quality
    const canvas = document.createElement('canvas');
    canvas.height = baseHeight * scaler;
    canvas.width = baseWidth * scaler;
    return new Promise((resolve, reject) => {
        const scaleHeight = baseHeight * scaler;
        const scaleWidth = baseWidth * scaler;
        let svgString = new XMLSerializer()
            .serializeToString(svgElem)
            .replace(/height="[0-9]+px"/, `height="${scaleHeight}px"`)
            .replace(/width="[0-9]+px"/, `width="${scaleWidth}px"`);
        const ctx = canvas.getContext('2d');
        const DOMURL = window.URL || window.webkitURL;
        const img = new Image();
        const svg = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = DOMURL.createObjectURL(svg);
        img.onload = () => {
            if (ctx) ctx.drawImage(img, 0, 0);
            imageData = canvas.toDataURL('image/png', 1);
            DOMURL.revokeObjectURL(url);
            if (imageData !== null) {
                resolve({ data: imageData, width: baseWidth, height: baseHeight, format: 'PNG' });
            } else {
                reject({ data: null, width: 0, height: 0, format: 'PNG' });
            }
        };
        img.src = url;
    });
}

function messageCoalesce(strings) {
    let string;
    for (let i = 0; i < strings.length; i++) {
        string = strings[i];
        if (typeof string === 'string' && !!string) return string.toString();
        if (typeof string === 'object' && string !== null && string.toString().length > 0) return string.toString();
    }
}

let analogLogoCanvas = null;
function addAnalogImage(doc, width = 60, downscaleFactor = 3) {
    return new Promise((resolve) => {
        let hasCanvasFn = () => {
            let dataUrl = analogLogoCanvas.toDataURL('image/jpeg', 0.9);
            let outHeight = (width * analogLogoCanvas.height) / analogLogoCanvas.width; // Make proportional
            doc.addImage(dataUrl, 'JPEG', MARGIN, MARGIN, width, outHeight);
            resolve();
        };

        if (!analogLogoCanvas) {
            // Retrieve the Analog image and run function when finished
            let analogImage = document.createElement('img');
            console.log('we are retrieving the analog image');
            analogImage.onload = () => {
                console.log('we have loaded the analog image');
                analogLogoCanvas = document.createElement('canvas');
                analogLogoCanvas.height = analogImage.height / downscaleFactor;
                analogLogoCanvas.width = analogImage.width / downscaleFactor;
                let ctx = analogLogoCanvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, analogLogoCanvas.width, analogLogoCanvas.height);
                analogLogoCanvas
                    .getContext('2d')
                    .drawImage(analogImage, 0, 0, analogLogoCanvas.width, analogLogoCanvas.height);
                hasCanvasFn();
            };
            analogImage.style.display = 'none';
            analogImage.src = 'images/Analog_Devices_Logo.svg.png';
        } else {
            hasCanvasFn();
        }
    });
}

function dropDoubleBytes(str) {
    let chars = [];

    for (var i = 0, n = str.length; i < n; i++) {
        if (str.charCodeAt(i) > 255) {
            chars.push(str.substr(i, 1));
        }
    }
    for (i = 0; i < chars.length; i++) {
        let char = chars[i];
        str = str.replace(char, '');
    }
    return str;
}

// Watch for jqplot to be drawn, so it can be scraped
const drawnPromise = function (stuffToDo = function () {}) {
    return new Promise((resolve) => {
        const jqplot = window.jQuery && window.jQuery.jqplot;
        if (jqplot && Array.isArray(jqplot.postDrawHooks)) {
            const hooks = jqplot.postDrawHooks;
            // Remove any existing export hooks by index (splice) - should only be 0-1 of these
            hooks
                .filter((h) => h && h.forExport === true)
                .forEach((h) => {
                    const idx = hooks.indexOf(h);
                    if (idx > -1) hooks.splice(idx, 1);
                });
        }
        let f = () => resolve(); // Add a function that resolves this promise
        f.forExport = true;
        window.jQuery.jqplot.postDrawHooks.push(f);

        // do stuff after post-draw hook is initalized.
        stuffToDo();
    });
};

// #endregion

// Create a detached element for rendering the canvas comfortably offscreen
let detachedChartEl = document.createElement('div');
detachedChartEl.id = 'detachedChartEl';
detachedChartEl.style.cssText = 'height: 500px; width: 500px; position: absolute; top: -2000px; left: -2000px';
document.body.appendChild(detachedChartEl);

export default class PdfExportService extends Service {
    @service intl;
    @service('data') model;
    @service errorHandling;
    @service plotDataService;
    @service urlData;
    @service tabService;

    //#region zoom

    // watch document scaling
    currentZoom = 1;
    zoomInvalid = false;
    zoomWasChanged = false;

    checkZoom(initialCheck = false) {
        let currentZoom = window.devicePixelRatio;

        // On subsequent resizes, check to see if the user changed the zoom
        if (!initialCheck) {
            let oldZoom = this.currentZoom;
            if (oldZoom != currentZoom) {
                this.set('zoomWasChanged', true);
                console.log('zoom was changed');
            }
        }
        this.set('currentZoom', currentZoom);
        this.set('zoomInvalid', currentZoom < 1);

        if (currentZoom < 1) this.set('zoomWasInvalid', true);
    }

    //#endregion zoom

    init() {
        super.init();
        console.log('initialized pdf-export service');

        $(window).on('resize', $.proxy(this.checkZoom, this, null)); // Re-check on resize, which captures zoom change
        this.checkZoom(true); // check immediately
        window.pdfExport = this; // for testing
    }

    //#region For saving external charts and chart messages

    tabs = {}; // not best practice

    //#endregion

    //#region generate pdf

    generatePdf() {
        let _model = this.model;
        let doc = new jsPDF('p', 'mm', 'letter'); // "p" for portrait, "mm", "letter"
        doc.addFileToVFS('ARIAL-normal.ttf', arial);
        doc.addFileToVFS('ARIALBD-bold.ttf', arialBold);
        doc.addFileToVFS('ARIALI-italic.ttf', arialItalic);
        doc.addFont('ARIAL-normal.ttf', 'ARIAL', 'normal');
        doc.addFont('ARIALBD-bold.ttf', 'ARIAL', 'bold');
        doc.addFont('ARIALI-italic.ttf', 'ARIAL', 'italic');
        let offset = 65;
        let left = 0; // for columns
        let page = 1;
        let fin = PrettyNum.eng3(_model.frequency);

        let part, driverPart;

        console.log('addAnalogImage start');
        console.time('addAnalogImage');
        addAnalogImage(doc, 50)
            .then(() => {
                console.log('addAnalogImage end');
                console.timeEnd('addAnalogImage');
                // Header text
                doc.setFontSize(FONT_SIZE_TITLE).text('Summary Report for Precision ADC Driver Tool', 15, 50);

                // "View configuration"
                doc.setFontSize(FONT_SIZE_NORMAL).text('View configuration at', 15, 60);

                // URL
                let url = window.location.href;
                url = url.replace('tab=4', 'tab=1');
                let splitUrl = doc.splitTextToSize(url, 170);

                doc.setTextColor(0, 0, 255); // blue
                for (let i = 0; i < splitUrl.length; i++) {
                    doc.textWithLink(splitUrl[i], 15, offset, { url });
                    offset += 5;
                }
                doc.setTextColor(0); // black
            })
            .then(() => {
                // main code for converting the circuit svg to an image for the pdf
                let mainSvg = document.querySelector('.svg-container svg');
                let returnSVG = convertSVGToImage(mainSvg);
                return returnSVG;
            })
            .then((svgImage) => {
                let pageWidth = doc.internal.pageSize.getWidth(); // get page width so the image can be scaled to fit the page
                let pageHeight = doc.internal.pageSize.getHeight(); // get page height so the position of the image can be adjusted
                let maxSvgHeight = 140; // Max height is 140mm for SVG to fit on page
                let scalerByWidth = pageWidth / svgImage.width;
                let scalerByHeight = maxSvgHeight / svgImage.height;
                let scaler = Math.min(scalerByWidth, scalerByHeight);
                let xPosition = (pageWidth - svgImage.width * scaler) / 2; // center the image
                let yOffset = pageHeight - svgImage.height * scaler - 20; // -20 compensates for the footer
                doc.addImage(
                    svgImage.data,
                    svgImage.format,
                    xPosition, //centered
                    yOffset,
                    svgImage.width * scaler,
                    svgImage.height * scaler,
                );

                offset += 10;

                // "Circuit specifications"
                doc.setFontSize(FONT_SIZE_H1).text('Circuit Specifications:', 15, offset);
                offset += 10;
                let rootOffset = offset; // for returning to later
                left = 0; // for columns
                doc.setFontSize(FONT_SIZE_NORMAL);

                // input column
                doc.setFont('ARIAL', 'bold').text('Input', 15, offset); // This may be a good place for split lines.
                offset += 5;
                doc.setFont('ARIAL', 'normal').text(
                    ` • ${_model.selectedSourceType.description.toString()}\n` +
                        ` • Fin = ${fin}Hz\n` +
                        (_model.multiplexed ? ' • Multiplexed input' : ''), // Optional, only if multiplexed.
                    15,
                    offset,
                );

                // Driver column
                offset = rootOffset;
                left = 55;
                driverPart = _model.selectedDriver.partCore;
                let driverUrl = `${this.urlData.analogBaseProductsUrl}/${driverPart}.html`;
                doc.setFont('ARIAL', 'bold')
                    .text('Driver - ', left, offset)
                    .setTextColor(0, 0, 255)
                    .textWithLink(driverPart, left + 15, offset, { url: driverUrl });
                doc.setTextColor(0);
                offset += 5;
                doc.setFont('ARIAL', 'normal').text(
                    ` • ${_model.selectedTopology.description} configuration\n` +
                        ` • +Vs = ${PrettyNum.eng3(_model.vSupplyPos)}V\n` +
                        ` • -Vs = ${PrettyNum.eng3(_model.vSupplyNeg)}V`,
                    left,
                    offset,
                );

                if (_model.filterEnabled) {
                    // Filter column
                    offset = rootOffset;
                    left = 108;
                    doc.setFont('ARIAL', 'bold').text('Filter', left, offset);
                    offset += 5;
                    doc.setFont('ARIAL', 'normal');
                    let ohm = ` • Rext = ${PrettyNum.eng3(_model.rext)}`; // Insert Ohm character after this
                    let ohmLength = doc.getTextWidth(ohm);
                    doc.text(ohm + '\n' + ` • Cext = ${PrettyNum.eng3(_model.cext)}F`, left, offset);
                    let ohmCharacterData = this.makeOhmCharacter().toDataURL(); // png url is default
                    // let outHeight = width * canvas.height / canvas.width; // Make proportional
                    let dim = (24 / 30) * 3.5; // convert size in pixels to size in mm (using 300ppi standard page resolution)
                    doc.addImage(ohmCharacterData, 'PNG', left + ohmLength, offset - dim, dim, dim);
                }

                // ADC column
                offset = rootOffset;
                left = _model.filterEnabled ? 150 : 108;
                part = _model.selectedAdc.partCore;
                let partUrl = `${this.urlData.analogBaseProductsUrl}/${part}.html`;
                doc.setFont('ARIAL', 'bold')
                    .text('ADC - ', left, offset)
                    .setTextColor(0, 0, 255)
                    .textWithLink(part, left + 12, offset, { url: partUrl });
                doc.setTextColor(0);
                offset += 5;
                doc.setFont('ARIAL', 'normal').text(
                    ` • Sample Rate = ${PrettyNum.eng3(_model.samplingRate)}SPS\n` +
                        ` • Vref = ${PrettyNum.eng4(_model.vref)}V\n` +
                        (_model.showVariant ? ' • HiZ mode enabled' : ''), // optional: Hi-Z variant (only expects Hi-Z at this time)
                    left,
                    offset,
                );

                // Add main chart diagram
                offset += 10; // This is as far up as the chart can be placed, unless it's drawn before the table.
                // doc.addImage(schemaCanvas.toDataURL('image/jpeg', 0.8), 'JPEG', 15, offset, 180, 180);

                // Footer
                this.addFooter(doc, page++);

                return hash({
                    ac: this.saveChartByName('acChart'),
                    dc: this.saveChartByName('dcChart'),
                    noise: this.saveChartByName('noiseChart'),
                    muxed: this.saveChartByName('muxedChart'),
                });
            })
            .then((jqplotData) => {
                // For charts
                let chartWidth = 120; // control chart's width
                let chartLeft = 40;
                let chartTop = 70;

                offset = 15;
                left = 15;

                //#region Page 2, Input Settling
                if (!this.model.selectedAdc.settlingPanelMessageIntl) {
                    // Settling simulation is not disabled
                    doc.addPage();

                    // Header and header data
                    doc.setFontSize(FONT_SIZE_H1).text('ADC Input Settling Simulation', left, offset);
                    offset += 10;

                    if (!this.errorHandling.vSupplyError) {
                        doc.setFontSize(FONT_SIZE_NORMAL).text(
                            ` • ADC Sample Period = ${PrettyNum.eng3(_model.samplePeriod)}s\n` +
                                ` • ADC Conversion Time = ${PrettyNum.eng3(
                                    _model.inputSettlingArrays.conversionTime,
                                )}s\n` +
                                ` • ADC Acquisition Time = ${PrettyNum.eng3(
                                    _model.inputSettlingArrays.acquisitionTime,
                                )}s\n` +
                                ` • RC Filter Bandwidth = ${PrettyNum.eng3(_model.filterBandwidth)}Hz\n` +
                                ` • 1 LSB = ${PrettyNum.eng3(_model.oneLsb)}V`,
                            left,
                            offset,
                        );
                    } else {
                        this.writeError(doc, this.intl.t('labels.unable_to_calc'), offset);
                    }

                    // Check for error and display if they are there
                    {
                        let rcBwWarn = this.errorHandling.bwWarnings.rcBw.toString();
                        let settlingRingingWarn = this.errorHandling.bwWarnings.settlingRinging.toString();
                        let error = messageCoalesce([rcBwWarn, settlingRingingWarn]);
                        if (error) {
                            // Only triggers for non-null non-"" values
                            // let width = 100;
                            // doc.setFontType('italic').setTextColor(255, 0, 0).setFontSize(FONT_SIZE_NORMAL - 1)
                            //     .text(doc.splitTextToSize(error, width), MARGIN_RIGHT - width, offset)
                            //     .setFontType('normal').setTextColor(0).setFontSize(FONT_SIZE_NORMAL);

                            offset += 25;
                            let errorLines = this.writeError(doc, error, offset);
                            offset += errorLines * 5 + 10;
                            chartTop = offset;
                        }
                    }

                    // For chart info overlays
                    let overlayTop = 15;
                    let overlayLeft = 70;

                    // DC Chart
                    if (jqplotData.dc && jqplotData.dc.data) {
                        doc.setFontSize(FONT_SIZE_H2).text('Kickback Simulation – DC Input Signal', left, chartTop - 5);
                        let dc = jqplotData.dc,
                            kickback = PrettyNum.eng3(_model.inputSettlingArrays.voltsKickbackDc, { scale: 0 }),
                            lsbs = mathRound.compute([_model.inputSettlingArrays.lsbMaxDc], {
                                step: 0.5,
                                func: 'ceil',
                                adjustedStep: true,
                            });
                        doc.addImage(
                            dc.data,
                            'PNG',
                            chartLeft,
                            chartTop,
                            chartWidth,
                            (chartWidth * dc.height) / dc.width,
                        );
                        doc.setFontSize(FONT_SIZE_NORMAL).text(
                            `Kickback ~ ${kickback}V\nSettling Error = ${lsbs} LSBs`,
                            chartLeft + overlayLeft,
                            chartTop + overlayTop,
                        );
                    }

                    let muxedTab = this.tabService.tabs.items.settling.find(
                            (tab) => tab.index === Constants.TAB_INDEX.SETTLING.MUXED,
                        ),
                        acTab = this.tabService.tabs.items.settling.find(
                            (tab) => tab.index === Constants.TAB_INDEX.SETTLING.AC,
                        ),
                        showMultiplexedChart = jqplotData.muxed && !muxedTab.hide,
                        showAcChart = jqplotData.ac && !acTab.hide;

                    if (showAcChart || showMultiplexedChart) {
                        // New page.
                        chartTop = 30;
                        this.addFooter(doc, page++);
                        doc.addPage();

                        // AC Chart
                        if (showAcChart) {
                            let ac = jqplotData.ac,
                                kickback = PrettyNum.eng3(_model.inputSettlingArrays.voltsKickbackSin, { scale: 0 }),
                                lsbs = mathRound.compute([_model.inputSettlingArrays.lsbMaxSin], {
                                    step: 0.5,
                                    func: 'ceil',
                                    adjustedStep: true,
                                });

                            doc.setFontSize(FONT_SIZE_H2).text(
                                'Kickback Simulation – AC Input Signal',
                                left,
                                chartTop - 5,
                            );
                            doc.addImage(
                                ac.data,
                                'PNG',
                                chartLeft,
                                chartTop,
                                chartWidth,
                                (chartWidth * ac.height) / ac.width,
                            );
                            doc.setFontSize(FONT_SIZE_NORMAL).text(
                                `Kickback ~ ${kickback}V\nSettling Error = ${lsbs} LSBs`,
                                chartLeft + overlayLeft,
                                chartTop + overlayTop,
                            );
                            chartTop = 150; // increment in case "muxed" exists
                        }

                        // Muxed chart
                        if (showMultiplexedChart) {
                            let muxed = jqplotData.muxed,
                                kickback = PrettyNum.eng3(_model.inputSettlingArrays.voltsKickbackMuxed, { scale: 0 }),
                                lsbs = mathRound.compute([_model.inputSettlingArrays.lsbMaxMuxed], {
                                    step: 0.5,
                                    func: 'ceil',
                                    adjustedStep: true,
                                });

                            doc.setFontSize(FONT_SIZE_H2).text(
                                'Kickback Simulation – Multiplexed Input Signal',
                                left,
                                chartTop - 5,
                            );
                            doc.addImage(
                                jqplotData.muxed.data,
                                'PNG',
                                chartLeft,
                                chartTop,
                                chartWidth,
                                (chartWidth * muxed.height) / muxed.width,
                            );
                            doc.setFontSize(FONT_SIZE_NORMAL).text(
                                `Kickback ~ ${kickback}V\nSettling Error = ${lsbs} LSBs`,
                                chartLeft + overlayLeft,
                                chartTop + overlayTop,
                            );
                        }
                    }

                    this.addFooter(doc, page++);
                }

                //#endregion

                // Page 3/4, Noise page
                doc.addPage();

                offset = 15;
                doc.setFontSize(FONT_SIZE_H1).text('Noise', left, offset);
                offset += 10;
                let sysnoise = PrettyNum.eng3(_model.noiseCalculations.total.totalVrms),
                    snr = PrettyNum.eng4(_model.noiseCalculations.total.totalSnr, { scale: 1 });
                doc.setFontSize(FONT_SIZE_NORMAL).text(
                    ` • SNR = ${snr}dB\n` + ` • System Noise = ${sysnoise}Vrms`,
                    left,
                    offset,
                );

                // Check for error and display if they are there
                {
                    let systemNoiseWarn = this.errorHandling.systemNoiseWarn.toString();
                    let wbNoiseError = this.errorHandling.wbNoiseError.toString();
                    let noiseError = messageCoalesce([systemNoiseWarn, wbNoiseError]);

                    // for test
                    // noiseError = "This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. <p>This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. This is going to be a really long message. It should span multiple paragraphs and be a good way to test how it behaves. "

                    if (noiseError) {
                        // Only triggers for non-null non-"" values
                        offset += 15;
                        // TODO: writeError no longer needs to show things in the right margin.
                        let noiseErrorLines = this.writeError(doc, noiseError, offset);
                        offset += noiseErrorLines * 5 + 5;
                    } else {
                        offset += 40;
                    }
                }

                doc.setFontSize(FONT_SIZE_H1).text('System Noise Contributions', left, offset);
                offset += 10;

                // Generate noise table
                {
                    //#region colors
                    let tbTop = offset - 5; // due to text starting at bottom-right corner of offset, not top-right
                    let tbLeft = 15;
                    let tbWidth = 135; // table width

                    let top = tbTop + 5; // text top
                    //let os = 0;             // Text offset
                    let c1 = tbLeft + 5,
                        c2 = tbLeft + 55,
                        c3 = tbLeft + 85,
                        c4 = tbLeft + 110; // column positions
                    let ibPlus =
                        _model.noiseCalculations.driver && _model.noiseCalculations.driver.ibPlus
                            ? _model.noiseCalculations.driver.ibPlus.sourceInputDensity
                            : null;
                    let ibMinus =
                        _model.noiseCalculations.driver && _model.noiseCalculations.driver.ibMinus
                            ? _model.noiseCalculations.driver.ibMinus.sourceInputDensity
                            : null;

                    let tbHeaderHeight = 10;
                    let tbTotalHeight = 5;
                    let tbSourceHeight = _model.showOptionalSourceSettings ? 5 : 0;
                    let tbDriverTotalHeight = 5;
                    let tbVnHeight = 5;
                    let tbIbPlusHeight = ibPlus ? 5 : 0;
                    let tbIbMinusHeight = ibMinus ? 5 : 0;
                    let tbRfHeight = _model.rfRemoved ? 0 : 5;
                    let tbRgHeight = _model.rgInf ? 0 : 5;
                    let tbRfiltHeight = _model.filterEnabled ? 5 : 0;
                    let tbAdcHeight = 5;

                    let yPos = tbTop;
                    let tbDriverItemsHeight = tbVnHeight + tbIbPlusHeight + tbIbMinusHeight + tbRfHeight + tbRgHeight;

                    let tab = {
                        left: 0,
                        mid: 3,
                        right: 6,
                    };

                    let fontStyle = {
                        normal: 'normal',
                        bold: 'bold',
                    };

                    let addTableRow = (r, g, b, rowHeight, extraHeight = 0) => {
                        if (rowHeight > 0) {
                            doc.setFillColor(r, g, b);
                            doc.rect(tbLeft, yPos, tbWidth, rowHeight + extraHeight, 'F');
                            yPos += rowHeight + extraHeight;
                        }
                    };

                    let addTableRowData = (column1, column2, column3, column4, rowHeight, tab, fontStyle) => {
                        if (rowHeight > 0) {
                            doc.setFont('ARIAL', fontStyle);
                            doc.text(tab + c1, top, column1);
                            doc.text(tab + c2, top, column2);
                            doc.text(tab + c3, top, column3);
                            doc.text(tab + c4, top, column4);
                            top += rowHeight;
                        }
                    };

                    // "table" rectangle drawing
                    addTableRow(199, 201, 204, tbHeaderHeight, 1);
                    addTableRow(222, 225, 229, tbTotalHeight);
                    addTableRow(198, 228, 213, tbSourceHeight);
                    addTableRow(177, 190, 209, tbDriverTotalHeight);
                    addTableRow(217, 228, 244, tbDriverItemsHeight);
                    addTableRow(177, 190, 209, tbRfiltHeight);
                    addTableRow(209, 171, 175, tbAdcHeight);

                    // Lines
                    doc.line(c2 - 5, tbTop, c2 - 5, yPos);
                    doc.line(c3 - 5, tbTop, c3 - 5, yPos);
                    doc.line(c4 - 5, tbTop, c4 - 5, yPos);
                    //#endregion

                    doc.setFontSize(11);

                    // Column headers
                    addTableRowData('', 'Noise\nVrms', 'Noise\n%', 'SNR\ndB', tbHeaderHeight, tab.left, fontStyle.bold);

                    // Data
                    addTableRowData(
                        'Total Noise',
                        PrettyNum.eng3(_model.noiseCalculations.total.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.total.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.total.totalSnr, { scale: 1 }),
                        tbTotalHeight,
                        tab.left,
                        fontStyle.bold,
                    );

                    addTableRowData(
                        'Source',
                        PrettyNum.eng3(_model.noiseCalculations.source.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.source.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.source.totalSnr, { scale: 1 }),
                        tbSourceHeight,
                        tab.mid,
                        fontStyle.bold,
                    );

                    addTableRowData(
                        'Driver',
                        PrettyNum.eng3(_model.noiseCalculations.driver.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.totalSnr, { scale: 1 }),
                        tbDriverTotalHeight,
                        tab.mid,
                        fontStyle.bold,
                    );

                    addTableRowData(
                        'Vn',
                        PrettyNum.eng3(_model.noiseCalculations.driver.vn.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.vn.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.vn.totalSnr, { scale: 1 }),
                        tbVnHeight,
                        tab.right,
                        fontStyle.normal,
                    );

                    addTableRowData(
                        'Ib+',
                        PrettyNum.eng3(_model.noiseCalculations.driver.ibPlus.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.ibPlus.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.ibPlus.totalSnr, { scale: 1 }),
                        tbIbPlusHeight,
                        tab.right,
                        fontStyle.normal,
                    );

                    addTableRowData(
                        'Ib-',
                        PrettyNum.eng3(_model.noiseCalculations.driver.ibMinus.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.ibMinus.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.ibMinus.totalSnr, { scale: 1 }),
                        tbIbMinusHeight,
                        tab.right,
                        fontStyle.normal,
                    );

                    addTableRowData(
                        'Rf',
                        PrettyNum.eng3(_model.noiseCalculations.driver.rf.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.rf.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.rf.totalSnr, { scale: 1 }),
                        tbRfHeight,
                        tab.right,
                        fontStyle.normal,
                    );

                    addTableRowData(
                        'Rg',
                        PrettyNum.eng3(_model.noiseCalculations.driver.rg.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.driver.rg.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.driver.rg.totalSnr, { scale: 1 }),
                        tbRgHeight,
                        tab.right,
                        fontStyle.normal,
                    );

                    if (_model.filterEnabled) {
                        addTableRowData(
                            'Rfilt',
                            PrettyNum.eng3(_model.noiseCalculations.rfilt.totalVrms),
                            PrettyNum.eng3(_model.noiseCalculations.rfilt.totalPerc),
                            PrettyNum.eng4(_model.noiseCalculations.rfilt.totalSnr, { scale: 1 }),
                            tbRfiltHeight,
                            tab.mid,
                            fontStyle.bold,
                        );
                    }

                    addTableRowData(
                        'ADC',
                        PrettyNum.eng3(_model.noiseCalculations.adc.totalVrms),
                        PrettyNum.eng3(_model.noiseCalculations.adc.totalPerc),
                        PrettyNum.eng4(_model.noiseCalculations.adc.totalSnr, { scale: 1 }),
                        tbAdcHeight,
                        tab.mid,
                        fontStyle.bold,
                    );

                    offset = top;
                }

                // Page 4, THD/Distortion (optional)

                // page pre-creation error handling
                let showThdPage = true;
                {
                    let error = messageCoalesce([
                        this.errorHandling.disabledTHDTabError,
                        this.errorHandling.noiseSubtabError,
                    ]);

                    // These are the errors that will prevent the page from showing.
                    if (error) {
                        showThdPage = false;

                        // Display error message on the bottom of the current page.
                        offset += 10;
                        doc.setFont('ARIAL', 'normal');
                        this.writeError(doc, error, offset);
                    }
                }

                if (showThdPage) {
                    // Otherwise display new distortion page
                    this.addFooter(doc, page++);
                    doc.addPage();

                    offset = MARGIN;
                    left = MARGIN;
                    doc.setFontSize(FONT_SIZE_H1).text('Distortion', left, offset);
                    offset += 10;
                    let thd = PrettyNum.eng3(this.plotDataService.noiseArrays.systemThdAtF),
                        sinad = PrettyNum.eng3(this.plotDataService.noiseArrays.sinadAtF),
                        enob = PrettyNum.eng3(this.plotDataService.noiseArrays.enobAtF);
                    doc.setFontSize(FONT_SIZE_NORMAL).text(
                        ` • THD @ ${fin}Hz = ${thd}dB\n` +
                            ` • SINAD @ ${fin}Hz = ${sinad}dB\n` +
                            ` • ENOB @ ${fin}Hz = ${enob} bits`,
                        left,
                        offset,
                    );
                    offset += 20;

                    // Add distortion error if required
                    {
                        let thdFreqMessage = this.thdFreqMessage;
                        let thdDriverMessage = this.thdDriverMessage;
                        let thdMessage = this.thdMessage;
                        let distortionError = messageCoalesce([thdFreqMessage, thdDriverMessage, thdMessage]);

                        // // testing
                        // distortionError = longLorem
                        // distortionError = null;
                        // // testing

                        if (distortionError) {
                            let lines = this.writeError(doc, distortionError, offset);
                            offset += lines * 5 + 10;
                            chartTop = offset;
                        } else {
                            chartTop = 70;
                        }
                    }

                    // Add another specific error, if required
                    {
                        let thdAdjustmentWarning = this.model.thdAdjustmentWarning;
                        // thdAdjustmentWarning = false;
                        if (thdAdjustmentWarning) {
                            offset -= 5;
                            let noise_thd_error_message = this.i18n.t('messages.noise_thd').toString();
                            let lines = this.writeError(doc, noise_thd_error_message, offset);

                            // update offset and new chart top
                            offset += lines * 5 + 10;
                            chartTop = offset;
                        }
                    }

                    // For charts

                    // Noise Chart
                    doc.setFontSize(FONT_SIZE_H2).text(
                        'Contributions to System THD vs. Input Frequency',
                        left,
                        chartTop - 5,
                    );
                    let noise = jqplotData.noise;
                    if (!!noise && !!noise.data) {
                        // if both are truthy
                        doc.addImage(
                            noise.data,
                            'PNG',
                            left,
                            chartTop,
                            chartWidth,
                            (chartWidth * noise.height) / noise.width,
                        );
                    }
                }
                this.addFooter(doc, page++);

                // Done
                doc.save(`Precision ADC Driver Tool Summary - ${part}+${driverPart}.pdf`); // prompt save
            })
            .catch((e) => {
                console.log('an error occurred');
                console.log(e);
                console.log(e.toString());
                //debugger;
            });
    }

    writeError(doc, errorMessage, offset) {
        let width = MARGIN_RIGHT - MARGIN;
        let htmlRegex = /<[^>]*>/;

        // Allow split by paragraph
        errorMessage = errorMessage
            .split(htmlRegex) // Split by paragraph markers
            .map((l) => l.trim())
            .filter((l) => l.length > 0) // Remove extra white space and discard any empty areas (ie first paragraph)
            .join('\n\n'); // Instead of paragraphs use double newline

        let splitLines = doc.splitTextToSize(errorMessage, width);

        // let width = 80;
        doc.setFont('ARIAL', 'italic')
            .setTextColor(255, 0, 0)
            .text(splitLines, MARGIN_RIGHT - width, offset)
            .setFont('ARIAL', 'normal')
            .setTextColor(0);

        return splitLines.length;
    }

    makeOhmCharacter() {
        let canvas = document.createElement('canvas');
        canvas.height = 24;
        canvas.width = 24;
        canvas.style.cssText = 'width: 20px; height: 20px';
        let context = canvas.getContext('2d');
        // document.body.appendChild(canvas);
        context.font = '30px helvetica';
        //context.clearRect(0,0,100,100);
        context.fillText('Ω', 0, 23);
        return canvas;
    }

    addFooter(doc, page) {
        doc.setFont('ARIAL', 'normal').setFontSize(FONT_SIZE_NORMAL);
        let top = MARGIN_BOTTOM;

        let event = new Date();
        let options = { year: 'numeric', month: 'long', day: 'numeric' };
        let date = event.toLocaleDateString('en-US', options);

        doc.text(dropDoubleBytes(date), 15, top); // Call dropDoubleBytes to fix IE. Otherwise there are LTR control characters in date.
        doc.setTextColor(0, 0, 255); // blue

        // this returns width, if we want to underline.
        doc.text(this.urlData.adcDriverUrl, MARGIN_CENTER, top, 'center'); // Where footer in original is
        doc.setTextColor(0);
        doc.text('Pg. ' + page, MARGIN_RIGHT, top, 'right'); // align to right
    }

    /*
        Examples:
        saveChartByName('muxedChart').then(u=>console.log(u))

        ChartInTabs selects tabs.dcChart,
        tabs.noiseChart, etc
    */
    saveChartByName(chartInTabs) {
        return new Promise((resolve) => {
            // Find selected chart info
            if (!this.tabs[chartInTabs]) {
                resolve(null);
                return;
            }
            let selector = this.tabs[chartInTabs].chartId;
            let chart = document.getElementById(selector);
            if (!chart || !chart.parentElement) {
                // reject('missing tab for chart ' + chartInTabs);
                resolve(null);
                return;
            }
            let chartParent = chart.parentElement;

            // Move chart to detached chart element
            let detachedChartEl = document.getElementById('detachedChartEl');
            detachedChartEl.appendChild(chart);

            // This executes a Draw and waits for it to complete.
            drawnPromise(() => {
                this.tabs[chartInTabs].draw(true);
            }).then(() => {
                // Save the output for now.
                let $t = window.jQuery('#' + selector),
                    canvas = null;

                // Trim legend
                Object.values(document.querySelectorAll(`#${selector} tr.jqplot-table-legend`))
                    .filter(
                        (item) =>
                            !(item && item.children.length === 2 && item.children[1].textContent) ||
                            item.children[1].textContent.includes('Series'),
                    )
                    .forEach((item) => item.remove());

                if ($t) canvas = $t.jqplotToImageCanvas();

                // tests
                //window.$t = $t;
                //document.body.appendChild(canvas)

                // Return chart to original location.
                chartParent.appendChild(chart);

                resolve({
                    height: canvas ? canvas.height : null,
                    width: canvas ? canvas.width : null,
                    data: canvas ? canvas.toDataURL('image/jpeg', 1) : null,
                    //canvas: canvas,
                });
            });
        }); //Promise
    }

    //#endregion generate pdf
}
